import CollectionProductPicker from '@/components/products/CollectionProductPicker'
import AddOnSetPicker from '@/components/products/AddOnSetPicker'
import { DetailWell } from '@/components/patterns/detail-page'
import {
  CollectionMembershipBadge,
  getCollectionMembershipBadgeToneClassName,
} from '@/components/collections/CollectionBadges'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { mergeEditedMedia, selectFirstValidAssetMedia } from '@/lib/media-editing'
import {
  PhotoManagerDefaultUploadDestination,
  PhotoManagerSelectionMode,
} from '@/lib/photo-manager-config'
import queryClient from '@/lib/queryClient'
import {
  collectionQueries,
  useCreateCollectionMutation,
  useDeleteCollectionMutation,
  useUpdateCollectionMutation,
} from '@/lib/services/collections/collections.query'
import { normalizeOccasionIds } from '@/lib/services/occasions/occasions.utils'
import { useMerchandisingTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.query'
import {
  flattenMerchandisingTagGroups,
  splitMerchandisingTagIds,
} from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { collection } from '@/lib/services/collections/collections.schema'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import { conditionalObject, getErrorMessage, handleDragEnd, slugify } from '@/lib/utils'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { useStore } from '@tanstack/react-form'
import { useNavigate, useParams } from '@tanstack/react-router'
import {
  CalendarDays,
  Camera,
  Check,
  EyeOff,
  Flower2,
  Info,
  PackagePlus,
  Pencil,
  Tags,
} from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import CreateActionButton from '@/components/patterns/create-action-button'
import FieldInfo from '../form/FieldInfo'
import ImageUploader from '@/components/media/ImageUploader'
import UploadedMediaPreviewTile from '@/components/media/UploadedMediaPreviewTile'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import CollectionProductsList from './CollectionProductsList'
import DeleteButton from './DeleteButton'
import SaveBar from './SaveBar'

const buildEmptyCollection = ({ isGlobalOrg = false } = {}) => ({
  name: '',
  slug: '',
  is_published: !isGlobalOrg,
  collection_origin: 'custom',
  collection_membership_type: 'manual',
  smart_rule: null,
  smart_sort_mode: 'automatic',
  is_protected: false,
  media: [],
  occasion_ids: [],
  season_ids: [],
  sync_occasion_product_membership: false,
  products: [],
  add_on_sets: [],
})

const buildCollectionFormValues = (collection, emptyCollection) => ({
  ...emptyCollection,
  ...collection,
  occasion_ids: normalizeOccasionIds(collection),
  season_ids: normalizeOccasionIds({
    occasion_ids: collection?.season_ids,
    occasions: collection?.seasons,
  }),
  sync_occasion_product_membership: false,
})

const collectionVisibilityOptions = [
  {
    id: 'false',
    name: 'Hidden',
    selectedTone: 'warning',
    start: (
      <span className="inline-flex items-center" aria-hidden="true">
        <EyeOff className="size-3.5" />
      </span>
    ),
  },
  {
    id: 'true',
    name: 'Visible',
    selectedTone: 'success',
    start: (
      <span className="inline-flex items-center" aria-hidden="true">
        <Check className="size-3.5" />
      </span>
    ),
  },
]

const collectionPublishClassNames = {
  container: 'grid w-full max-w-sm grid-cols-2',
  button: 'h-9 min-w-0 justify-center gap-1.5 px-3 text-sm',
  label: 'font-medium',
  unselectedButton:
    'bg-background text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-55 dark:bg-card',
}

const collectionShowPagePublishClassNames = {
  container: 'grid w-full min-w-0 grid-cols-2',
  button: 'min-w-0 items-center justify-center gap-1.5 px-2 leading-none',
}

function getCollectionPublishClassNames(showPageTreatment) {
  if (!showPageTreatment) {
    return collectionPublishClassNames
  }

  return collectionShowPagePublishClassNames
}

const SMART_SORT_AUTOMATIC = 'automatic'
const SMART_SORT_CUSTOM = 'custom'
const DATE_BACKED_OCCASION_KIND = 'date_backed'

function productCardDescription({
  isDesignersChoiceSmartCollection,
  isOccasionSmartCollection,
  isSubscriptionCollection,
  isSmartCollection,
  occasionLabels,
  smartSortMode,
}) {
  if (isSubscriptionCollection) {
    return 'Subscription packages appear here automatically when products are saved as subscription packages.'
  }

  if (isDesignersChoiceSmartCollection) {
    return smartSortMode === SMART_SORT_CUSTOM
      ? "Products appear when Designer's Choice is turned on for the product. Custom order is active, so seasonal ordering is paused."
      : "Products appear when Designer's Choice is turned on for the product. Automatic order prioritizes upcoming occasions and everyday demand."
  }

  if (isOccasionSmartCollection) {
    return occasionLabels.length === 1
      ? `Products tagged for ${occasionLabels[0]} appear here automatically. Use Manage Products or edit product occasions to change membership, then drag the list into the storefront order you want.`
      : 'Products tagged for these occasions or seasons appear here automatically. Use Manage Products or edit product occasions to change membership, then drag the list into the storefront order you want.'
  }

  return isSmartCollection
    ? "Products appear here automatically when their settings match this collection's rule."
    : 'Choose the products shoppers should see, then drag them into the order you want.'
}

function getOccasionDetailsForIds({ occasionIds = [], occasions = [], collection }) {
  const optionDetails = new Map(
    occasions.map((occasion) => [
      Number(occasion.id),
      {
        id: Number(occasion.id),
        kind: occasion.kind,
        label: occasion.label ?? occasion.name,
      },
    ])
  )
  const collectionDetails = new Map(
    [...(collection?.occasions ?? []), ...(collection?.seasons ?? [])].map((occasion) => [
      Number(occasion.id),
      {
        id: Number(occasion.id),
        kind: occasion.kind,
        label: occasion.label ?? occasion.name,
      },
    ])
  )

  return occasionIds
    .map(
      (occasionId) =>
        optionDetails.get(Number(occasionId)) ?? collectionDetails.get(Number(occasionId))
    )
    .filter((occasion) => occasion?.label)
}

function getOccasionLabelsForIds(args) {
  return getOccasionDetailsForIds(args).map((occasion) => occasion.label)
}

function protectedOccasionRuleDescription(occasionLabels) {
  if (occasionLabels.length === 1) {
    return 'Occasion cannot be modified for this built-in collection.'
  }

  return 'Occasions cannot be modified for this built-in collection.'
}

function collectionWebAddressHelper(slug) {
  const normalizedSlug = slug?.trim() || 'your-collection'

  return `Shoppers visit /collections/${normalizedSlug} on your website.`
}

function formatProductCount(count) {
  return `${count} product${count === 1 ? '' : 's'}`
}

function CollectionOccasionHelpText() {
  return (
    <span className="inline-flex items-start gap-1.5 leading-4">
      <Info className="mt-px size-3.5 shrink-0 text-interaction" aria-hidden="true" />
      <span>Occasions and seasons help shoppers find this collection on your website.</span>
    </span>
  )
}

const CollectionForm = ({ initialData, showPageTreatment = false }) => {
  const id = useId()
  const isGlobalOrg = useIsGlobalOrg()
  const { isReadOnly } = useIsReadOnly()
  const path = usePathHelper()
  const navigate = useNavigate()
  const { orgId } = useParams({ strict: false })
  const [open, setOpen] = useState(false)
  const [isAddOnSetsDialogOpen, setIsAddOnSetsDialogOpen] = useState(false)
  const [isImageUploaderOpen, setIsImageUploaderOpen] = useState(false)

  const createCollectionMutation = useCreateCollectionMutation()
  const updateCollectionMutation = useUpdateCollectionMutation()
  const deleteCollectionMutation = useDeleteCollectionMutation({ cleanupOnSuccess: false })
  const organizationId = initialData?.organization_id ?? orgId
  const { data: merchandisingTagGroups = [] } = useMerchandisingTagGroups({ organizationId })
  const emptyCollection = buildEmptyCollection({ isGlobalOrg })
  const merchandisingTagOptions = flattenMerchandisingTagGroups(merchandisingTagGroups)

  const mutation = initialData ? updateCollectionMutation : createCollectionMutation
  const isSubscriptionCollection = isSubscriptionPackageCollection(initialData)
  const isSmartCollection = initialData?.collection_membership_type === 'smart'
  const isDesignersChoiceSmartCollection =
    isSmartCollection && initialData?.smart_rule === 'designer_choice'
  const isOccasionSmartCollection = isSmartCollection && initialData?.smart_rule === 'occasion_tags'
  const isProtectedCollection =
    Boolean(initialData?.is_protected) || initialData?.collection_origin === 'platform'
  const isProtectedOccasionCollection = isProtectedCollection && isOccasionSmartCollection
  const initialOccasionIds = [
    ...normalizeOccasionIds(initialData),
    ...normalizeOccasionIds({
      occasion_ids: initialData?.season_ids,
      occasions: initialData?.seasons,
    }),
  ]
  const selectedOccasionLabels = getOccasionLabelsForIds({
    occasionIds: initialOccasionIds,
    occasions: merchandisingTagOptions,
    collection: initialData,
  })
  const selectedOccasionDetails = getOccasionDetailsForIds({
    occasionIds: initialOccasionIds,
    occasions: merchandisingTagOptions,
    collection: initialData,
  })
  const form = useAppForm({
    defaultValues: initialData
      ? buildCollectionFormValues(initialData, emptyCollection)
      : emptyCollection,
    validators: {
      onSubmit: collection,
      onSubmitAsync: async ({ value, formApi }) => {
        const submittedValue = isSubscriptionCollection
          ? {
              ...value,
              add_on_sets: [],
              occasion_ids: [],
              season_ids: [],
            }
          : value

        try {
          const response = await mutation.mutateAsync(
            conditionalObject(!!initialData, { collectionId: initialData?.id }, submittedValue)
          )
          const action = initialData ? 'updated' : 'created'
          toast.success(`Collection ${action} successfully`)

          if (initialData) {
            if (!initialData.organization_id) {
              setTimeout(() => formApi.reset())

              return
            }

            try {
              const refreshedCollection = await queryClient.fetchQuery({
                ...collectionQueries.show({
                  organizationId: initialData.organization_id,
                  collectionId: initialData.id,
                }),
                staleTime: 0,
              })

              setTimeout(() =>
                formApi.reset(buildCollectionFormValues(refreshedCollection, emptyCollection))
              )
            } catch {
              setTimeout(() => formApi.reset())
            }
          } else {
            void navigate({
              ...path.collections.show(response?.id).link,
              ignoreBlocker: true,
            })
          }
        } catch (error) {
          console.error(error)
          const action = initialData ? 'update' : 'creation'
          toast.error(`Collection ${action} failed`, {
            description: getErrorMessage(error),
          })
          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })
  const selectedCollectionOccasionIds = useStore(
    form.store,
    (state) => state.values.occasion_ids ?? []
  )
  const selectedCollectionSeasonIds = useStore(form.store, (state) => state.values.season_ids ?? [])
  const selectedMerchandisingTagIds = [
    ...selectedCollectionOccasionIds,
    ...selectedCollectionSeasonIds,
  ]
  const updateCollectionMerchandisingTags = (nextTagIds) => {
    const { occasionIds, seasonIds } = splitMerchandisingTagIds(
      nextTagIds,
      merchandisingTagOptions,
      {
        occasionIds: selectedCollectionOccasionIds,
        seasonIds: selectedCollectionSeasonIds,
      }
    )

    form.setFieldValue('occasion_ids', occasionIds)
    form.setFieldValue('season_ids', seasonIds)
  }

  const permission = initialData ? 'edit' : 'create'

  return (
    <div className="max-w-4xl">
      <form.AppForm>
        <form.Form>
          <div className="grid gap-y-6">
            <Card>
              <CardContent className="space-y-6">
                <form.AppField
                  name="name"
                  listeners={{
                    onBlur: ({ value }) => {
                      if (value && !form.getFieldValue('slug')) {
                        form.setFieldValue('slug', slugify(value))
                      }
                    },
                  }}
                >
                  {(field) => (
                    <field.TextInput
                      label="Name"
                      placeholder="Birthday Flowers"
                      disabled={isReadOnly || isSubscriptionCollection}
                    />
                  )}
                </form.AppField>
                <form.AppField name="slug">
                  {(field) => (
                    <div className="space-y-1">
                      <field.TextInput
                        label="Web address"
                        placeholder="birthday-flowers"
                        disabled={isReadOnly || isSubscriptionCollection}
                      />
                      <Typography variant="caption" className="block">
                        {collectionWebAddressHelper(field.state.value)}
                      </Typography>
                    </div>
                  )}
                </form.AppField>
                <form.AppField name="is_published">
                  {(field) => (
                    <div className="space-y-2">
                      <field.RadioButtonGroup
                        appearance={showPageTreatment ? 'segmented' : 'default'}
                        id={`${id}-is_published`}
                        label="Visible on website"
                        className={showPageTreatment ? 'max-w-md' : ''}
                        classNames={getCollectionPublishClassNames(showPageTreatment)}
                        displayValue={field.state.value}
                        joined={showPageTreatment}
                        options={collectionVisibilityOptions}
                        parseValue={(value) => value === 'true'}
                        size="sm"
                        unselectedVariant="outline"
                      />
                    </div>
                  )}
                </form.AppField>
                {!isSubscriptionCollection ? (
                  <form.AppField name="occasion_ids">
                    {(field) => (
                      <>
                        {isProtectedOccasionCollection ? (
                          <div className="space-y-2">
                            <Typography variant="label" className="block">
                              Occasions
                            </Typography>
                            <DetailWell compact>
                              <div className="flex flex-wrap gap-2">
                                {selectedOccasionDetails.length ? (
                                  selectedOccasionDetails.map((occasion) => {
                                    const isScheduled = occasion.kind === DATE_BACKED_OCCASION_KIND
                                    const Icon = isScheduled ? CalendarDays : Tags

                                    return (
                                      <Badge
                                        key={`${occasion.id}-${occasion.label}`}
                                        variant={isScheduled ? 'warning' : 'periwinkle'}
                                        size="choice"
                                      >
                                        <Icon
                                          className="size-3.5 shrink-0 text-muted-foreground"
                                          aria-hidden="true"
                                        />
                                        <span>{occasion.label}</span>
                                      </Badge>
                                    )
                                  })
                                ) : (
                                  <Typography variant="supporting">
                                    No occasion rule selected.
                                  </Typography>
                                )}
                              </div>
                              <Typography variant="supporting" className="mt-2 block">
                                {protectedOccasionRuleDescription(selectedOccasionLabels)}
                              </Typography>
                            </DetailWell>
                            <Typography variant="caption" className="block">
                              <CollectionOccasionHelpText />
                            </Typography>
                          </div>
                        ) : (
                          <field.OccasionPicker
                            label="Occasions"
                            labelDescription={<CollectionOccasionHelpText />}
                            options={merchandisingTagOptions}
                            value={selectedMerchandisingTagIds}
                            writeToField={false}
                            mode="multiple"
                            dialogDescription="Choose the occasions and seasons where this collection should appear."
                            onValueChange={updateCollectionMerchandisingTags}
                          />
                        )}
                      </>
                    )}
                  </form.AppField>
                ) : null}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="inline-flex items-center justify-between">
                  <div className="inline-flex items-center">
                    <Camera className="mr-2 inline" />
                    <Typography variant="h4">Image</Typography>
                  </div>
                  <div>
                    <CreateActionButton
                      context="inline"
                      id={`${id}-add-image`}
                      size="sm"
                      variant="outline"
                      onClick={() => setIsImageUploaderOpen(true)}
                    >
                      New Image
                    </CreateActionButton>
                  </div>
                </CardTitle>
                <CardDescription>
                  Use a clear seasonal image that helps shoppers recognize this collection on your
                  website.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form.AppField name="media">
                  {(field) => (
                    <div>
                      <FieldInfo />
                      <ul className="mb-5">
                        {field.state.value?.map((media, index) => (
                          <li key={media?.id} className="mx-2 inline-flex items-center">
                            <UploadedMediaPreviewTile
                              media={media}
                              alt={media?.name || 'Collection image'}
                              caption={media?.name}
                              onRemove={!isReadOnly ? () => field.removeValue(index) : null}
                              onEditComplete={
                                !isReadOnly
                                  ? (editedMedia) => {
                                      const currentMedia = Array.isArray(field.state.value)
                                        ? field.state.value
                                        : []
                                      const nextMedia = [...currentMedia]
                                      nextMedia[index] = mergeEditedMedia(
                                        nextMedia[index],
                                        editedMedia
                                      )
                                      field.setValue(nextMedia)
                                    }
                                  : null
                              }
                              removeLabel={`Remove ${media?.name || 'collection image'}`}
                              editUploadUsage={MediaUploadUsage.StorefrontHero.Value}
                              editAttachToPhotoManager
                              allowFocalPoint={index === 0 && !isReadOnly}
                              onFocalPointChange={(updatedMedia) => {
                                const currentMedia = Array.isArray(field.state.value)
                                  ? field.state.value
                                  : []
                                const nextMedia = [...currentMedia]
                                nextMedia[index] = mergeEditedMedia(nextMedia[index], updatedMedia)
                                field.setValue(nextMedia)
                              }}
                              wrapperClassName="w-36 flex-shrink-0"
                              frameClassName="aspect-square w-full rounded-lg"
                              imageClassName="h-full w-full rounded-lg object-cover"
                            />
                          </li>
                        ))}
                      </ul>
                      <ImageUploader
                        open={isImageUploaderOpen}
                        uploadUsage={MediaUploadUsage.StorefrontHero.Value}
                        defaultUploadDestination={PhotoManagerDefaultUploadDestination.BannerImages}
                        selectionMode={PhotoManagerSelectionMode.Single}
                        onRequestClose={() => setIsImageUploaderOpen(false)}
                        onSelectAssets={(assets = []) => {
                          const { selectedMedia, insertedCount, skippedCount } =
                            selectFirstValidAssetMedia(assets, {
                              uploadUsage: MediaUploadUsage.StorefrontHero.Value,
                            })

                          if (selectedMedia) {
                            field.setValue([selectedMedia])
                          }

                          return {
                            insertedCount,
                            skippedCount,
                          }
                        }}
                        onUploadSuccess={(_, response) => {
                          if (response?.body) {
                            field.setValue([response.body])
                          }
                        }}
                      />
                    </div>
                  )}
                </form.AppField>
              </CardContent>
            </Card>

            <form.Subscribe
              selector={(state) => ({
                smartSortMode: state.values.smart_sort_mode,
                occasionIds: state.values.occasion_ids,
                seasonIds: state.values.season_ids,
                productCount: state.values.products?.length ?? 0,
              })}
            >
              {({ smartSortMode, occasionIds, seasonIds, productCount }) => {
                const normalizedSmartSortMode =
                  smartSortMode === SMART_SORT_CUSTOM ? SMART_SORT_CUSTOM : SMART_SORT_AUTOMATIC
                const currentOccasionLabels = getOccasionLabelsForIds({
                  occasionIds: [...occasionIds, ...seasonIds],
                  occasions: merchandisingTagOptions,
                  collection: initialData,
                })
                const allowProductReorder =
                  !isSubscriptionCollection &&
                  (!isSmartCollection ||
                    isOccasionSmartCollection ||
                    (isDesignersChoiceSmartCollection &&
                      normalizedSmartSortMode === SMART_SORT_CUSTOM))
                const smartSortTooltip =
                  normalizedSmartSortMode === SMART_SORT_CUSTOM
                    ? 'Return to automatic seasonal and demand-based ordering.'
                    : 'Arrange these products yourself. Automatic seasonal ordering will stop.'
                const handleSmartSortModeClick = () => {
                  form.setFieldValue(
                    'smart_sort_mode',
                    normalizedSmartSortMode === SMART_SORT_CUSTOM
                      ? SMART_SORT_AUTOMATIC
                      : SMART_SORT_CUSTOM
                  )
                }

                return (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between gap-3">
                        <div className="inline-flex items-center">
                          <Flower2 className="mr-2 inline" />
                          <Typography variant="h4">Products</Typography>
                          <Badge variant="muted" size="xs" className="ml-2">
                            {formatProductCount(productCount)}
                          </Badge>
                        </div>
                        <div className="ml-auto flex shrink-0 items-center justify-end gap-2">
                          <CollectionMembershipBadge
                            collection={initialData}
                            automaticLabel="Fills automatically"
                            manualLabel="Filled by hand"
                            className={getCollectionMembershipBadgeToneClassName({
                              collection: initialData,
                            })}
                          />
                          {isDesignersChoiceSmartCollection ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={isReadOnly}
                                  onClick={handleSmartSortModeClick}
                                >
                                  {normalizedSmartSortMode === SMART_SORT_CUSTOM
                                    ? 'Return to Automatic Order'
                                    : 'Use Custom Order'}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{smartSortTooltip}</TooltipContent>
                            </Tooltip>
                          ) : isOccasionSmartCollection ||
                            (!isSmartCollection && !isSubscriptionCollection) ? (
                            <div>
                              <Dialog open={open} onOpenChange={setOpen}>
                                <DialogTrigger asChild>
                                  <CreateActionButton
                                    context="inline"
                                    id={`${id}-add-products`}
                                    size="sm"
                                    variant="outline"
                                  >
                                    {isOccasionSmartCollection
                                      ? 'Manage Products'
                                      : 'Change Products'}
                                  </CreateActionButton>
                                </DialogTrigger>
                                <DialogContent
                                  size="2xlarge"
                                  viewport="contained"
                                  className="!flex flex-col"
                                >
                                  <DialogHeader className="shrink-0">
                                    <DialogTitle>
                                      {isOccasionSmartCollection
                                        ? 'Manage products'
                                        : 'Choose products'}
                                    </DialogTitle>
                                    <DialogDescription>
                                      {isOccasionSmartCollection
                                        ? 'Choose the products shoppers should see here. Saving will update their occasion tags, and you can drag the list into the storefront order you want.'
                                        : 'Pick the products shoppers should see in this collection. You can drag the final list into the storefront order you want.'}
                                    </DialogDescription>
                                  </DialogHeader>
                                  {/* Removing and adding this to the DOM again keeps "initialData" in sync */}
                                  {open && (
                                    <CollectionProductPicker
                                      initialData={form.getFieldValue('products')}
                                      onConfirm={(products) => {
                                        form.setFieldValue('products', products)
                                        if (isOccasionSmartCollection) {
                                          form.setFieldValue(
                                            'sync_occasion_product_membership',
                                            true
                                          )
                                        }
                                        void form.validateField('products', 'change')
                                        setOpen(false)
                                      }}
                                      onCancel={() => setOpen(false)}
                                    />
                                  )}
                                </DialogContent>
                              </Dialog>
                            </div>
                          ) : null}
                        </div>
                      </CardTitle>
                      <CardDescription>
                        {productCardDescription({
                          isDesignersChoiceSmartCollection,
                          isOccasionSmartCollection,
                          isSubscriptionCollection,
                          isSmartCollection,
                          occasionLabels: currentOccasionLabels,
                          smartSortMode: normalizedSmartSortMode,
                        })}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form.AppField name="products">
                        {(field) =>
                          allowProductReorder ? (
                            <DndContext
                              collisionDetection={closestCenter}
                              onDragEnd={handleDragEnd(field)}
                            >
                              <FieldInfo />
                              <SortableContext items={field.state.value}>
                                <CollectionProductsList
                                  products={field.state.value ?? []}
                                  permission={permission}
                                  onRemoveProduct={!isSmartCollection ? field.removeValue : null}
                                />
                              </SortableContext>
                            </DndContext>
                          ) : (
                            <>
                              <FieldInfo />
                              <CollectionProductsList
                                products={field.state.value ?? []}
                                permission={permission}
                                allowReorder={false}
                              />
                            </>
                          )
                        }
                      </form.AppField>
                    </CardContent>
                  </Card>
                )
              }}
            </form.Subscribe>
            {!isSubscriptionCollection ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-3">
                    <div className="inline-flex items-center">
                      <PackagePlus className="mr-2 inline" />
                      <Typography variant="h4">Add-ons</Typography>
                    </div>
                    {!isReadOnly ? (
                      <Dialog open={isAddOnSetsDialogOpen} onOpenChange={setIsAddOnSetsDialogOpen}>
                        <DialogTrigger asChild>
                          <CreateActionButton
                            context="inline"
                            id={`${id}-add-on-sets`}
                            size="sm"
                            variant="outline"
                          >
                            Change Add-on Collections
                          </CreateActionButton>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add-on Collections</DialogTitle>
                            <DialogDescription>
                              Choose the Add-on Collections shoppers can pick from for products in
                              this collection.
                            </DialogDescription>
                          </DialogHeader>
                          {isAddOnSetsDialogOpen ? (
                            <form.AppField name="add_on_sets">
                              {(field) => (
                                <AddOnSetPicker
                                  organizationId={organizationId}
                                  initialData={field.state.value ?? []}
                                  onConfirm={(addOnSets) => {
                                    field.setValue(addOnSets)
                                    void form.validateField('add_on_sets', 'change')
                                    setIsAddOnSetsDialogOpen(false)
                                  }}
                                  onCancel={() => setIsAddOnSetsDialogOpen(false)}
                                />
                              )}
                            </form.AppField>
                          ) : null}
                        </DialogContent>
                      </Dialog>
                    ) : null}
                  </CardTitle>
                  <CardDescription>
                    Assign Add-on Collections to products in this collection.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form.AppField name="add_on_sets">
                    {(field) => {
                      const addOnSets = field.state.value ?? []

                      return addOnSets.length > 0 ? (
                        <ul className="divide-y rounded-md border">
                          {addOnSets.map((set) => (
                            <li
                              key={set.id}
                              className="flex items-center justify-between gap-3 px-3 py-2"
                            >
                              <div className="flex min-w-0 items-center gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <a
                                      href={path.addOnSets.show(set.id).href}
                                      target="_blank"
                                      rel="noreferrer"
                                      aria-label={`Open ${set.name} Add-on Collection`}
                                      className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                      <Pencil className="size-4" />
                                    </a>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit Add-on Collection</TooltipContent>
                                </Tooltip>
                                <span className="min-w-0 truncate font-medium">{set.name}</span>
                              </div>
                              <Typography variant="supporting">
                                {set.add_ons_count ?? set.add_ons?.length ?? 0} add-on
                                {(set.add_ons_count ?? set.add_ons?.length ?? 0) === 1 ? '' : 's'}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Typography variant="supporting">
                          No Add-on Collections selected.
                        </Typography>
                      )
                    }}
                  </form.AppField>
                </CardContent>
              </Card>
            ) : null}
          </div>
          <Can do={permission} on="collections">
            <SaveBar mutation={mutation} />
          </Can>
        </form.Form>
      </form.AppForm>
      {!!initialData && !isProtectedCollection && (
        <Can do="delete" on="collections">
          <DeleteButton
            id={initialData?.id}
            model="Collection"
            mutation={deleteCollectionMutation}
            path={path.collections.index()}
            replace
            onDeleted={deleteCollectionMutation.cleanupDeletedCollection}
          />
        </Can>
      )}
    </div>
  )
}

export default CollectionForm
